import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { MagnifyingGlass } from "@phosphor-icons/react";
import { api } from "../lib/api";
import ProductCard from "../components/ProductCard";

const CATS = [
  { key: "all", label: "All" },
  { key: "skincare", label: "Skincare" },
  { key: "fragrance", label: "Fragrance" },
  { key: "makeup", label: "Makeup" },
  { key: "haircare", label: "Haircare" },
];

export default function SearchPage() {
  const [params, setParams] = useSearchParams();
  const [q, setQ] = useState(params.get("q") || "");
  const [category, setCategory] = useState(params.get("category") || "all");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let cancel = false;
    setLoading(true);
    const t = setTimeout(async () => {
      try {
        const rows = await api.listProducts({
          q: q || undefined,
          category: category === "all" ? undefined : category,
          limit: 60,
        });
        if (!cancel) setItems(rows);
      } finally {
        if (!cancel) setLoading(false);
      }
    }, 200);
    return () => {
      cancel = true;
      clearTimeout(t);
    };
  }, [q, category]);

  useEffect(() => {
    const next = {};
    if (q) next.q = q;
    if (category && category !== "all") next.category = category;
    setParams(next, { replace: true });
  }, [q, category, setParams]);

  const summary = useMemo(() => {
    if (loading) return "Searching…";
    if (items.length === 0) return "No matches found.";
    return `${items.length} product${items.length === 1 ? "" : "s"}`;
  }, [items, loading]);

  return (
    <div className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-10 py-8 lg:py-12" data-testid="search-page">
      <div className="eyebrow">Catalogue</div>
      <h1 className="font-heading font-light text-4xl sm:text-5xl mt-2 tracking-tight">
        Search <span className="font-accent italic text-[#A66E4E]">any product</span>
      </h1>
      <p className="mt-3 text-[#6E6B65] max-w-xl">
        Query name, brand, or ingredient. Results ingest authorized sellers, parallel
        imports and all complaint signals.
      </p>

      <div className="mt-8 flex flex-col md:flex-row gap-px bg-[#E5E2DC] border border-[#E5E2DC]">
        <label className="flex items-center gap-3 bg-white px-4 py-3 flex-1">
          <MagnifyingGlass size={18} className="text-[#6E6B65]" />
          <input
            data-testid="search-input"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Chanel No. 5, Niacinamide, Aventus…"
            className="flex-1 bg-transparent outline-none text-base font-heading placeholder:text-[#6E6B65]"
          />
        </label>
        <div className="flex bg-white overflow-x-auto">
          {CATS.map((c) => (
            <button
              key={c.key}
              type="button"
              data-testid={`filter-${c.key}`}
              onClick={() => setCategory(c.key)}
              className={`px-4 py-3 text-xs tracking-[0.18em] uppercase font-semibold whitespace-nowrap transition-colors ${
                category === c.key
                  ? "bg-[#1C1B1A] text-white"
                  : "text-[#1C1B1A] hover:bg-[#EFECE6]"
              }`}
            >
              {c.label}
            </button>
          ))}
        </div>
      </div>

      <div className="mt-4 flex items-center justify-between">
        <span className="eyebrow">{summary}</span>
      </div>

      <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-px bg-[#E5E2DC] border border-[#E5E2DC]">
        {items.map((p) => (
          <ProductCard key={p.id} product={p} />
        ))}
        {!loading && items.length === 0 && (
          <div className="col-span-full bg-white p-10 text-center text-[#6E6B65]">
            No products match your query. Try another keyword.
          </div>
        )}
      </div>
    </div>
  );
}
